import React, { useContext, useEffect } from "react";
import { Stack, Grid, Group, Text } from "@mantine/core";
import FloatingLabelInput from "./floating-label-input";
import { DataContext } from "../context/DataContext";

const UkAddressInput = ({
  onAddressChange,
  value,
  stacked = false,
  enterKeyHint,
  disabled = false,
  ...props
}) => {
  const { text, data } = useContext(DataContext);

  // Controlled by the parent's form values (passed via `value`), falling back
  // to saved data, so the inputs survive unmount/remount between steps.
  const safeValue = value && typeof value === "object" ? value : {};
  const address = {
    addr: safeValue.addr ?? data?.addr ?? "",
    city: safeValue.city ?? data?.city ?? "",
    state: safeValue.state ?? data?.state ?? "",
    zip: safeValue.zip ?? data?.zip ?? "",
  };

  const handleInputChange = (field, fieldValue) => {
    onAddressChange({ ...address, [field]: fieldValue });
  };

  // Seed the parent's form values once on mount from the resolved address, so
  // navigating away and back never clears the inputs (see AddressInput).
  useEffect(() => {
    onAddressChange(address);
  }, []);

  return (
    <>
      <Group>
        <Text fz="xs" c="dimmed">
          {text?.general?.address || "Address"}
        </Text>
      </Group>

      <Stack spacing="xs">
        {props.error && (
          <Text fz="xs" c="red">
            Address is required
          </Text>
        )}
        <FloatingLabelInput
          placeholder=""
          label="Street Address"
          value={address.addr}
          onChange={(e) => handleInputChange("addr", e.currentTarget.value)}
          autoComplete="address-line1"
          enterKeyHint={enterKeyHint}
          disabled={disabled}
        />
        {(() => {
          const townInput = (
            <FloatingLabelInput
              placeholder=""
              label="Town/City"
              value={address.city}
              onChange={(e) => handleInputChange("city", e.currentTarget.value)}
              autoComplete="address-level2"
              enterKeyHint={enterKeyHint}
              disabled={disabled}
            />
          );

          const countyInput = (
            <FloatingLabelInput
              placeholder=""
              label="County"
              value={address.state}
              onChange={(e) => handleInputChange("state", e.currentTarget.value)}
              autoComplete="address-level1"
              enterKeyHint={enterKeyHint}
              disabled={disabled}
            />
          );

          if (stacked) {
            return (
              <>
                {townInput}
                {countyInput}
              </>
            );
          }

          return (
            <Grid gutter={8}>
              <Grid.Col span={6}>{townInput}</Grid.Col>
              <Grid.Col span={6}>{countyInput}</Grid.Col>
            </Grid>
          );
        })()}
        <FloatingLabelInput
          placeholder=""
          label="Postcode"
          value={address.zip}
          onChange={(e) => handleInputChange("zip", e.currentTarget.value)}
          autoComplete="postal-code"
          enterKeyHint={enterKeyHint}
          disabled={disabled}
        />
      </Stack>
    </>
  );
};

export default UkAddressInput;
